import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./Button";
import { ReactComponent as Exit } from "../assets/icons/exit.svg";
import { ADMIN_NAV } from "../config/adminNav";
import type { AdminNavGroupItem } from "../config/adminNav";
import {
  buildVisibleNav,
  collectGroupIds,
  isGroupActive,
  isGroupOpen,
  isNavPathActive,
  readOpenGroups,
  writeOpenGroups,
} from "../helpers/adminNav";
import { AdminNavLink } from "./adminNav/AdminNavLink";
import { AdminNavGroup } from "./adminNav/AdminNavGroup";

const GROUP_IDS = collectGroupIds(ADMIN_NAV);

const AdminNavigation: React.FC = () => {
  const { logout, hasPermission } = useAuth();
  const { pathname } = useLocation();

  const sections = useMemo(
    () => buildVisibleNav(ADMIN_NAV, hasPermission),
    [hasPermission]
  );

  // Read synchronously so the first paint already has the right groups open.
  const [overrides, setOverrides] = useState(() => readOpenGroups(GROUP_IDS));

  const toggleGroup = (group: AdminNavGroupItem) =>
    setOverrides((prev) => {
      // Negate the effective state, not the stored one: the first click on an
      // auto-opened group must close it rather than store a redundant `true`.
      const next = { ...prev, [group.id]: !isGroupOpen(prev, pathname, group) };
      writeOpenGroups(next);
      return next;
    });

  return (
    <nav className="h-full flex flex-col" aria-label="Admin">
      <div className="flex-1 min-h-0 overflow-y-auto thin-scrollbar pr-1">
        {sections.map((section) => (
          <section key={section.id} className={section.title ? "mt-6" : undefined}>
            {section.title && (
              <h3
                id={`adminnav-${section.id}`}
                className="px-4 mb-1 text-xs font-semibold uppercase tracking-wider text-gray-600"
              >
                {section.title}
              </h3>
            )}
            <ul
              className="flex flex-col gap-1"
              aria-labelledby={
                section.title ? `adminnav-${section.id}` : undefined
              }
            >
              {section.items.map((item) => (
                <li key={item.id}>
                  {item.kind === "group" ? (
                    <AdminNavGroup
                      group={item}
                      pathname={pathname}
                      isOpen={isGroupOpen(overrides, pathname, item)}
                      containsActive={isGroupActive(pathname, item)}
                      onToggle={() => toggleGroup(item)}
                    />
                  ) : (
                    <AdminNavLink
                      to={item.path}
                      label={item.label}
                      icon={item.icon}
                      isActive={isNavPathActive(pathname, item)}
                      variant="top"
                    />
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <div className="mt-auto pt-4">
        <Button
          variant="ghost"
          onClick={logout}
          className="text-red gap-2 flex items-center hover:text-red-400"
        >
          <Exit />
          Log out
        </Button>
      </div>
    </nav>
  );
};

export default AdminNavigation;
